import PropTypes from 'prop-types';
import parse from 'html-react-parser';

import useMediaQuery from '@material-ui/core/useMediaQuery';

import { FormattedMessage, useIntl } from 'react-intl';
import { useReducer, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core/styles';

import { PromptModal } from 'components/common/modals';
import { BasicDrawer, Secure2uDrawer, TacDrawer } from 'components/common/drawers';
import { DynoAlertModal } from 'components/dyno-template/modals';
import { DynoConfirmationCards, DynoResultDrawer } from 'components/dyno-template';

import {
  getTransactionDetails,
  formatTransactionResultBody,
  getTotalDisplayAmount,
  getSenderAccountDetails,
  getTacRequestDetail,
} from 'utils/transaction';
import { get } from 'utils/lodash';
import { formatErrorMessage } from 'utils/formatter';
import { hasLldDetail } from 'utils/transfer/swift/validation';
import { formatCommonResultBody } from 'utils/transfer/formatter';
import { formatSwiftResultBody } from 'utils/transfer/swift/formatter';
import { formatEWalletResultBody } from 'utils/transfer/e-wallet/formatter';
import { formatIntrabankResultBody } from 'utils/transfer/intrabank/formatter';
import { formatInterbankResultBody } from 'utils/transfer/interbank/formatter';
import { getVirtualAccountMonetaries } from 'utils/transfer/virtual-account/getter';
import { formatVirtualAccountResultBody } from 'utils/transfer/virtual-account/formatter';
import { favouriteFormAfterTransferDetailHandler } from 'utils/favourite/transfer/handler';
import { getInterbankMonetaries, getInterbankReceiverInfos } from 'utils/transfer/interbank/getter';
import { getSknRtgsBeneficiaryDetails, getSknRtgsMonetaryDetails } from 'utils/transfer/sknRtgs/getter';
import { getEWalletBeneficiaryDetails, getEWalletMonetaryDetails } from 'utils/transfer/e-wallet/getter';
import { getSwiftBeneficiaries, getSwiftLLDs, getSwiftMonetaries, getSwiftReceiverInfo } from 'utils/transfer/swift/getter';
import { getConfirmationDetail, getResultDetail, getMonetaryDetails, getRecurringDetails, getReceiverDetail } from 'utils/transfer/getter';

import {
  getIntrabankReceipt,
  getIntrabankResult,
  requestIntrabankTAC,
  getOwnTransferReceipt,
  getOwnTransferResult,
  getOwnTransferTac,
  getSknTransferTac,
  getSknTransferResult,
  getSknTransferReceipt,
  requestInterbankTAC,
  getInterbankResult,
  getInterbankReceipt,
  putIntrabankTac,
  putOwnTransferTac,
  putSknTransferTac,
  putInterbankTacResult,
  putRtgsTac,
  requestRtgsTAC,
  getRtgsResult,
  getRtgsReceipt,
  putVirtualAccountTac,
  requestVirtualAccountTac,
  getVirtualAccountResult,
  getVirtualAccountReceipt,
  putEWalletTac,
  requestEWalletTac,
  getEWalletResult,
  getEWalletReceipt,
  putSwiftTAC,
  requestSwiftTAC,
  getSwiftReceipt,
  getSwiftResult,
} from 'middleware/actions/transfer';

import {
  E_WALLET,
  INTERBANK,
  INTRABANK,
  OWN_ACCOUNT,
  RTGS_TRANSFER,
  SKN_TRANSFER,
  VIRTUAL_ACCOUNT,
  TRANSFER_RECURRING,
  SWIFT_TRANSFER,
  TRANSACTION_TRANSFER_TAB_VALUE,
  FAVOURITE_MAXIMUM_LIST,
} from 'settings/constants/transaction';
import { TAC_STEP_FINAL, TAC_STEP_INITIAL, TAC_STEP_RETRY } from 'settings/constants/tac';
import { MODAL_FULLSCREEN_SIZE as MOBILE_SCREEN_SIZE } from 'settings/constants/ui-control';
import isEmpty from 'lodash/isEmpty';
import { getCheckUserSecure2u, getPoolingSecure2u, getPushContentSecure2u } from 'middleware/actions/sec2u';
import { REJECT_TRANSACTION_SECURE2U_RESPONSE_CODE, SUCCESS_RESPONSE_CODE } from 'settings/constants/response-codes';

const TransferConfirmationContainer = ({
  userId,
  onEdit,
  onError,
  onChange,
  formDetail,
  transferType,
  transferMode,
  senderAccount,
  onAddFavourite,
  favouriteAccounts,
  beneficiaryType,
  onCancelTransfer,
  onAccountRefresh,
  transferFrequency,
  transferTacDetail,
  transactionCharges,
  confirmationDetail,
  swiftOperationTime,
  isFavouriteTransfer,
  transferResultDetail,
  beneficiaryCitizenStatus,
  handleTutorial,
  handleCloseCoachmark,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(MOBILE_SCREEN_SIZE));
  const [isPolling, setIsPolling] = useState(false);
  const [openDrawerErr, setOpenDrawerErr] = useState(false);
  const [openDrawerErrCheckUser, setOpenDrawerErrCheckUser] = useState(false);
  const prevIsTacRequestSuccess = useRef(false);
  const [backRetrySecure2u, setBackRetrySecure2u] = useState(false);
  const hasDispatchedRef = useRef(false);

  const {
    sknReceipt,
    rtgsReceipt,
    swiftReceipt,
    eWalletReceipt,
    intrabankReceipt,
    interbankReceipt,
    ownTransferReceipt,
    virtualAccountReceipt,
    pushContentSecure2u,
    poolingSecure2u,
    checkUserSecure2u,
  } = useSelector(
    state => ({
      swiftReceipt: state.TransferReducer.swiftReceipt,
      rtgsReceipt: state.TransferReducer.rtgsReceipt,
      eWalletReceipt: state.TransferReducer.eWalletReceipt,
      sknReceipt: state.TransferReducer.sknTransferReceipt,
      interbankReceipt: state.TransferReducer.interbankReceipt,
      intrabankReceipt: state.TransferReducer.intrabankReceipt,
      ownTransferReceipt: state.TransferReducer.ownTransferReceipt,
      virtualAccountReceipt: state.TransferReducer.virtualAccountReceipt,
      pushContentSecure2u: state.Sec2uReducer.pushContentSecure2u,
      poolingSecure2u: state.Sec2uReducer.poolingSecure2u,
      checkUserSecure2u: state.Sec2uReducer.checkUserSecure2u,
    }),
    shallowEqual
  );

  const [state, setState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    tacStep: TAC_STEP_INITIAL,
    isTacOpen: false,
    isResultOpen: false,
    isLeavePageBlocked: false,
    timestamp: '',
    isLimitMessageOpen: false,
    triggerPushContent: false,
  });

  const { tacStep, timestamp, isTacOpen, isResultOpen, isLeavePageBlocked, isLimitMessageOpen, triggerPushContent } = state;

  const transferReceipt = {
    [INTERBANK]: interbankReceipt,
    [INTRABANK]: intrabankReceipt,
    [OWN_ACCOUNT]: ownTransferReceipt,
    [SKN_TRANSFER]: sknReceipt,
    [RTGS_TRANSFER]: rtgsReceipt,
    [VIRTUAL_ACCOUNT]: virtualAccountReceipt,
    [E_WALLET]: eWalletReceipt,
    [SWIFT_TRANSFER]: swiftReceipt,
    default: {},
  };

  const { tacBean, isConfirmSuccess } = getConfirmationDetail(confirmationDetail, transferType);
  const { tacTimestamp, isTacRequestSuccess } = getTacRequestDetail(transferTacDetail);

  const result = getResultDetail(transferResultDetail, transferType);
  const { resultError, isTacLocked, isResultSuccess, transactionId, referenceNumber, isSwiftCutOffError, addFavObject, showLimitMessage } =
    result;

  const disableAddFavourite = favouriteAccounts.length >= FAVOURITE_MAXIMUM_LIST;
  const effectiveDateDisplay = get(confirmationDetail, 'effectiveDateDisplay', '');
  const isRecurring = transferMode === TRANSFER_RECURRING && !isEmpty(effectiveDateDisplay);
  const recurringDetails = isRecurring ? getRecurringDetails({ confirmationDetail, transferFrequency }) : [];
  const receiptPayload = { userId, transactionId };
  const tacRequestPayload = { userId, tacBean };
  const beneficiaryPayload = { confirmationDetail, referenceNumber, isResultSuccess, isRecurring };

  const receiptDetail = transferReceipt[transferType] || transferReceipt.default;
  const receipt = get(receiptDetail, 'receiptPdf', '');

  const transactionToLabel = formatMessage({ id: 'transaction.transfer.to' });
  // toAmountDisplay
  const totalAmount = getTotalDisplayAmount({ confirmationDetail, transferType });
  const accountNumber = get(confirmationDetail, `${transferType === SWIFT_TRANSFER ? 'fromAccount' : 'fromAccountNumber'}`, '');
  const senderDetails = getSenderAccountDetails(accountNumber, senderAccount, 'transaction.transfer.from', fullScreen);
  const lldDetails = hasLldDetail(transferType, confirmationDetail) ? getSwiftLLDs(confirmationDetail) : [];
  const isOwnTransfer = transferType === OWN_ACCOUNT;
  const receiverDetail =
    transferType === SWIFT_TRANSFER
      ? getSwiftReceiverInfo(confirmationDetail)
      : getReceiverDetail(confirmationDetail, transferType, isFavouriteTransfer);
  const toAmount = get(confirmationDetail, 'debitAmountDis', '');
  const toAmountRTOL = get(confirmationDetail, 'toAmountDis', '');
  const toAmountEwallet = get(confirmationDetail, 'totalPaymentDisplay', '');
  // eslint-disable-next-line no-nested-ternary
  const toAmountTransfer = transferType === INTERBANK ? toAmountRTOL : transferType === E_WALLET ? toAmountEwallet : toAmount;
  const serviceName = get(confirmationDetail, 'serviceName', '');

  const { responseCode: responsePushContent } = pushContentSecure2u;

  const beneficiary = {
    [INTRABANK]: getTransactionDetails(beneficiaryPayload),
    [OWN_ACCOUNT]: getTransactionDetails(beneficiaryPayload),
    [INTERBANK]: getInterbankReceiverInfos(beneficiaryPayload),
    [SKN_TRANSFER]: getSknRtgsBeneficiaryDetails(beneficiaryPayload),
    [RTGS_TRANSFER]: getSknRtgsBeneficiaryDetails(beneficiaryPayload),
    [VIRTUAL_ACCOUNT]: getTransactionDetails(beneficiaryPayload),
    [E_WALLET]: getEWalletBeneficiaryDetails(beneficiaryPayload, isFavouriteTransfer),
    [SWIFT_TRANSFER]: getSwiftBeneficiaries(beneficiaryPayload),
    default: [],
  };

  const monetary = {
    [INTRABANK]: getMonetaryDetails(confirmationDetail),
    [OWN_ACCOUNT]: getMonetaryDetails(confirmationDetail),
    [INTERBANK]: getInterbankMonetaries(confirmationDetail, senderAccount),
    [SKN_TRANSFER]: getSknRtgsMonetaryDetails(confirmationDetail),
    [RTGS_TRANSFER]: getSknRtgsMonetaryDetails(confirmationDetail),
    [VIRTUAL_ACCOUNT]: getVirtualAccountMonetaries(confirmationDetail),
    [E_WALLET]: getEWalletMonetaryDetails(confirmationDetail),
    [SWIFT_TRANSFER]: getSwiftMonetaries(confirmationDetail),
    default: [],
  };

  const monetaryDetails = monetary[transferType] || monetary.default;
  const beneficiaryDetails = beneficiary[transferType] || beneficiary.default;

  // Reset redux state before sending TAC to refresh tac request status
  const requestTacHandler = {
    [INTERBANK]: () => {
      dispatch(putInterbankTacResult());
      dispatch(requestInterbankTAC(tacRequestPayload));
    },
    [INTRABANK]: () => {
      dispatch(putIntrabankTac());
      dispatch(requestIntrabankTAC(tacRequestPayload));
    },
    [OWN_ACCOUNT]: () => {
      dispatch(putOwnTransferTac());
      dispatch(getOwnTransferTac(tacRequestPayload));
    },
    [SKN_TRANSFER]: () => {
      dispatch(putSknTransferTac());
      dispatch(getSknTransferTac(tacRequestPayload));
    },
    [RTGS_TRANSFER]: () => {
      dispatch(putRtgsTac());
      dispatch(requestRtgsTAC(tacRequestPayload));
    },
    [VIRTUAL_ACCOUNT]: () => {
      dispatch(putVirtualAccountTac());
      dispatch(requestVirtualAccountTac(tacRequestPayload));
    },
    [E_WALLET]: () => {
      dispatch(putEWalletTac());
      dispatch(requestEWalletTac(tacRequestPayload));
    },
    [SWIFT_TRANSFER]: () => {
      dispatch(putSwiftTAC());
      dispatch(requestSwiftTAC(tacRequestPayload));
    },
    default: () => console.error('No such request found!'),
  };

  const submitTacHandler = {
    [INTERBANK]: tacValue =>
      dispatch(
        getInterbankResult(formatInterbankResultBody({ userId, tacValue, confirmationDetail, transferTacDetail, isFavouriteTransfer }))
      ),
    [INTRABANK]: tacValue =>
      dispatch(
        getIntrabankResult(
          formatIntrabankResultBody({
            userId,
            tacValue,
            confirmationDetail,
            transferTacDetail,
            transferMode,
            transferFrequency,
            isFavouriteTransfer,
          })
        )
      ),
    [OWN_ACCOUNT]: tacValue =>
      dispatch(
        getOwnTransferResult(
          formatCommonResultBody(userId, tacValue, confirmationDetail, transferTacDetail, transferMode, transferFrequency, senderAccount)
        )
      ),
    [SKN_TRANSFER]: (tacValue, resultBody) => dispatch(getSknTransferResult(resultBody)),
    [RTGS_TRANSFER]: (tacValue, resultBody) => dispatch(getRtgsResult(resultBody)),
    [SWIFT_TRANSFER]: (tacValue, resultBody) =>
      dispatch(
        getSwiftResult(formatSwiftResultBody({ userId, tacValue, formDetail, detail: confirmationDetail, resultBody, transactionCharges }))
      ),
    [VIRTUAL_ACCOUNT]: tacValue =>
      dispatch(
        getVirtualAccountResult(
          formatVirtualAccountResultBody({ userId, tacValue, confirmationDetail, transferTacDetail, isFavouriteTransfer })
        )
      ),
    [E_WALLET]: tacValue =>
      dispatch(
        getEWalletResult(
          formatEWalletResultBody({
            userId,
            tacValue,
            transferTacDetail,
            confirmationDetail,
            senderAccount,
            formDetail,
            isFavouriteTransfer,
          })
        )
      ),
    default: () => console.error('No such request found!'),
  };

  const receiptHandler = {
    [INTERBANK]: () => dispatch(getInterbankReceipt(receiptPayload)),
    [INTRABANK]: () => dispatch(getIntrabankReceipt(receiptPayload)),
    [OWN_ACCOUNT]: () => dispatch(getOwnTransferReceipt(receiptPayload)),
    [SKN_TRANSFER]: () => dispatch(getSknTransferReceipt(receiptPayload)),
    [RTGS_TRANSFER]: () => dispatch(getRtgsReceipt(receiptPayload)),
    [VIRTUAL_ACCOUNT]: () => dispatch(getVirtualAccountReceipt(receiptPayload)),
    [E_WALLET]: () => dispatch(getEWalletReceipt(receiptPayload)),
    [SWIFT_TRANSFER]: () => dispatch(getSwiftReceipt(receiptPayload)),
    default: () => console.error('No such request found!'),
  };

  const handleNoticeClose = () => setState({ isLimitMessageOpen: false });

  const handleRequestTac = () => (requestTacHandler[transferType] || requestTacHandler.default)();
  const handleSecure2u = () => (requestTacHandler[transferType] || requestTacHandler.default)();
  const retrySecure2uHandler = () => dispatch(getCheckUserSecure2u());

  const handleSubmitTac = tacValue => {
    const resultBody = formatTransactionResultBody({
      userId,
      tacValue,
      confirmationDetail,
      transferType,
      transferTacDetail,
      transferMode,
      transferFrequency,
      beneficiaryType,
      beneficiaryCitizenStatus,
      isFavouriteTransfer,
    });

    return (submitTacHandler[transferType] || submitTacHandler.default)(tacValue, resultBody);
  };

  const handleGetReceipt = () => (receiptHandler[transferType] || receiptHandler.default)();

  const showErrorMessage = (errorCode, params) => onError(parse(formatErrorMessage(formatMessage, errorCode, params)));

  const handleAddNewFavourite = () => {
    onChange({ favouriteTransferType: transferType });
    // get transfer types (only for new fav from main transfer page)
    favouriteFormAfterTransferDetailHandler(transferType, userId, addFavObject);
    // lastly call handleOnAddFavourite() in index to open modal
    onAddFavourite(false);
  };

  const handleCloseDrawerErr = () => {
    setOpenDrawerErr(false);
    setOpenDrawerErrCheckUser(false);
    setBackRetrySecure2u(true);

    Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
    Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);
    // eslint-disable-next-line no-param-reassign
    Object.keys(transferTacDetail).forEach(key => delete transferTacDetail[key]);
    Object.keys(checkUserSecure2u).forEach(key => delete checkUserSecure2u[key]);

    setState({
      tacStep: TAC_STEP_INITIAL,
      isTacOpen: true,
    });
  };

  const memoizedProps = useMemo(
    () => ({
      isTacRequestSuccess,
      responsePushContent,
    }),
    [isTacRequestSuccess, responsePushContent]
  );

  const handleDispatch = useCallback(() => {
    const bodyRecurring = {
      userId,
      transactionType: serviceName,
      amount: toAmountTransfer,
      effectiveDateDisplay,
    };
    const bodyCommon = {
      userId,
      transactionType: serviceName,
      amount: toAmountTransfer,
      effectiveDateDisplay: isEmpty(effectiveDateDisplay) ? '' : effectiveDateDisplay,
    };

    dispatch(getPushContentSecure2u(isRecurring ? bodyRecurring : bodyCommon));
  }, [confirmationDetail]);

  const showPopup = () => {
    if (window.innerWidth > 769) handleTutorial(false, true, false);
    if (window.innerWidth < 769) handleTutorial(false, true, false);
  };

  useEffect(() => {
    Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
    Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);
    Object.keys(checkUserSecure2u).forEach(key => delete checkUserSecure2u[key]);
  }, []);

  useEffect(() => {
    if (isTacLocked) {
      setState({ isLeavePageBlocked: false });
    }
  }, [isTacLocked]);

  useEffect(() => {
    if (resultError) {
      showErrorMessage(resultError, swiftOperationTime);
    }
  }, [transferResultDetail, resultError]);

  useEffect(() => {
    if (isConfirmSuccess) {
      setState({ isTacOpen: true });
    }
  }, [isConfirmSuccess]);

  useEffect(() => {
    const shouldDispatch = isTacRequestSuccess && !prevIsTacRequestSuccess.current && !hasDispatchedRef.current;

    if (shouldDispatch) {
      handleDispatch();
      hasDispatchedRef.current = true;
    }

    prevIsTacRequestSuccess.current = isTacRequestSuccess;

    // only update timestamp if TAC request is successful
    if (isTacRequestSuccess && responsePushContent === SUCCESS_RESPONSE_CODE) {
      setState({ timestamp: tacTimestamp, tacStep: TAC_STEP_FINAL, isLeavePageBlocked: true });
      hasDispatchedRef.current = false;
    }
  }, [memoizedProps, handleDispatch]);
  // useEffect(() => {
  //   // only update timestamp if TAC request is successful
  //   if (isTacRequestSuccess) {
  //     setState({ timestamp: tacTimestamp, tacStep: TAC_STEP_FINAL, isLeavePageBlocked: true });
  //   }
  // }, [isTacRequestSuccess]);

  useEffect(() => {
    if (isResultSuccess) {
      setBackRetrySecure2u(false);
      setState({ isResultOpen: true, isLeavePageBlocked: false });
      // get new source account info after transaction is made.
      onAccountRefresh();
    }
  }, [isResultSuccess]);

  useEffect(() => {
    if (isSwiftCutOffError) {
      setState({ isLeavePageBlocked: false });
    }
  }, [isSwiftCutOffError]);

  useEffect(() => {
    if (showLimitMessage) {
      setState({ isLimitMessageOpen: true });
    }
  }, [showLimitMessage]);

  useEffect(() => {
    if (tacStep === TAC_STEP_RETRY) handleCloseCoachmark();

    if (tacStep === TAC_STEP_FINAL) {
      setBackRetrySecure2u(false);
      setIsPolling(true);
    }
    if (tacStep === TAC_STEP_INITIAL || tacStep === TAC_STEP_RETRY) {
      // eslint-disable-next-line no-param-reassign
      Object.keys(transferTacDetail).forEach(key => delete transferTacDetail[key]);
      setIsPolling(false);
    }
  }, [tacStep]);

  useEffect(() => {
    const { securityHash } = pushContentSecure2u;
    const { responseCode } = poolingSecure2u;

    if (responseCode === SUCCESS_RESPONSE_CODE) {
      setIsPolling(false);
      Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
      Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);

      handleSubmitTac('818888888');

      handleCloseCoachmark();
    }

    if (responseCode === REJECT_TRANSACTION_SECURE2U_RESPONSE_CODE) {
      handleCloseCoachmark();

      setOpenDrawerErr(true);
      setBackRetrySecure2u(true);
      setState({ isTacOpen: false });
      Object.keys(pushContentSecure2u).forEach(key => delete pushContentSecure2u[key]);
      Object.keys(poolingSecure2u).forEach(key => delete poolingSecure2u[key]);
      // eslint-disable-next-line no-param-reassign
      Object.keys(transferTacDetail).forEach(key => delete transferTacDetail[key]);
      setIsPolling(false);
    }

    let intervalId; // Declare intervalId outside the if block

    if (isPolling) {
      // Define the function to be called every 3 seconds
      const poolingDataSecure2u = () => {
        dispatch(
          getPoolingSecure2u({
            userId,
            securityHash,
          })
        );
      };

      // Set up the interval to call poolingDataSecure2u every 3 seconds
      intervalId = setInterval(poolingDataSecure2u, 3000);
    }

    // Cleanup function to clear the interval
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, poolingSecure2u, dispatch, userId, pushContentSecure2u]);

  useEffect(() => {
    const { responseCode, isNormal } = checkUserSecure2u;

    if (responseCode === SUCCESS_RESPONSE_CODE && isNormal) {
      handleSecure2u();
      setIsPolling(false);
      setState({ triggerPushContent: true });
      Object.keys(checkUserSecure2u).forEach(key => delete checkUserSecure2u[key]);
    }

    if (responseCode === SUCCESS_RESPONSE_CODE && !isNormal) setState({ isTacOpen: false });
    if (!isEmpty(checkUserSecure2u) && responseCode !== SUCCESS_RESPONSE_CODE) setOpenDrawerErrCheckUser(true);
  }, [checkUserSecure2u]);

  return (
    <>
      <DynoConfirmationCards
        onEdit={onEdit}
        result={result}
        tacStep={tacStep}
        lldDetails={lldDetails}
        totalAmount={totalAmount}
        frequency={transferFrequency}
        senderDetails={senderDetails}
        receiverDetail={receiverDetail}
        monetaryDetails={monetaryDetails}
        recurringDetails={recurringDetails}
        onAddFavourite={handleAddNewFavourite}
        beneficiaryDetails={beneficiaryDetails}
        transactionToLabel={transactionToLabel}
        isOwnTransfer={isOwnTransfer}
        hasFavouriteButton={!isOwnTransfer} // if not owntransfer show favbutton
        disableAddFavourite={disableAddFavourite}
        isDeleteTransaction={false}
      />

      {isResultSuccess ? (
        <DynoResultDrawer
          receipt={receipt}
          isOpen={isResultOpen}
          onClose={() => onCancelTransfer()}
          onDownload={handleGetReceipt}
          resultDetail={transferResultDetail}
          transactionType={TRANSACTION_TRANSFER_TAB_VALUE}
        />
      ) : (
        // <TacDrawer
        //   currentStep={tacStep}
        //   timestamp={timestamp}
        //   isOpen={isTacOpen}
        //   onRequestTac={handleRequestTac}
        //   onSubmitTac={handleSubmitTac}
        //   title={formatMessage({ id: 'transaction.transfer.tacTitle' })}
        //   description={parse(formatMessage({ id: 'transaction.transfer.tacDescription' }))}
        // />
        <Secure2uDrawer
          currentStep={tacStep}
          timestamp={timestamp}
          isOpen={isTacOpen}
          onRequestSecure2u={handleSecure2u}
          onSubmitTac={handleSubmitTac}
          onRetrySecure2u={retrySecure2uHandler}
          showPopup={showPopup}
          title={formatMessage({ id: 'transaction.transfer.tacTitle' })}
          onChange={setState}
          handleCloseCoachmark={handleCloseCoachmark}
          backRetrySecure2u={backRetrySecure2u}
        />
      )}

      <PromptModal
        isBlocked={isLeavePageBlocked}
        onCancelTransaction={onCancelTransfer}
        title={formatMessage({ id: 'transaction.leaveAlert.title' })}
        content={formatMessage({ id: 'transaction.leaveAlert.description' })}
        proceedLabel={formatMessage({ id: 'buttonText.ok' })}
        cancelLabel={formatMessage({ id: 'buttonText.cancel' })}
      />

      <DynoAlertModal
        isOpen={isLimitMessageOpen}
        onClose={handleNoticeClose}
        title={formatMessage({ id: 'transaction.transfer.swift.modal.limitTitle' })}
        description={parse(formatMessage({ id: 'transaction.transfer.swift.modal.limitMessage' }))}
      />

      <BasicDrawer className="error" isOpen={openDrawerErr || openDrawerErrCheckUser} hideBackdrop={false} onClose={handleCloseDrawerErr}>
        <h1 className="f5 lh-copy tc white">
          {openDrawerErrCheckUser && <FormattedMessage id="errorMessages.ERR_SERVER_ERROR" />}
          {openDrawerErr && <FormattedMessage id="errorMessages.ERR_SECURE2U" />}
        </h1>
      </BasicDrawer>
    </>
  );
};

TransferConfirmationContainer.defaultProps = {
  beneficiaryType: '',
  favouriteAccounts: [],
  transactionCharges: '',
  beneficiaryCitizenStatus: '',
  handleTutorial: null,
  handleCloseCoachmark: null,
};

TransferConfirmationContainer.propTypes = {
  beneficiaryType: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  favouriteAccounts: PropTypes.array,
  transactionCharges: PropTypes.string,
  formDetail: PropTypes.object.isRequired,
  onAddFavourite: PropTypes.func.isRequired,
  transferType: PropTypes.string.isRequired,
  transferMode: PropTypes.string.isRequired,
  senderAccount: PropTypes.object.isRequired,
  beneficiaryCitizenStatus: PropTypes.string,
  onAccountRefresh: PropTypes.func.isRequired,
  onCancelTransfer: PropTypes.func.isRequired,
  transferFrequency: PropTypes.string.isRequired,
  isFavouriteTransfer: PropTypes.bool.isRequired,
  transferTacDetail: PropTypes.object.isRequired,
  confirmationDetail: PropTypes.object.isRequired,
  swiftOperationTime: PropTypes.object.isRequired,
  transferResultDetail: PropTypes.object.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleTutorial: PropTypes.func,
  handleCloseCoachmark: PropTypes.func,
};

export default TransferConfirmationContainer;
